import { useState, useEffect } from 'react';
import { Machine, OfflineMachine, LocalHostConnection, get_preferred_connection } from '../../ts/Connections';
import { refreshMatchmakingData } from '../../ts/matchmakingRefresh';
import Grid from '@mui/material/Grid'
import { Typography, FormControl, InputLabel, Select, MenuItem, Alert, AlertTitle, Box } from '@mui/material'
import MatchmakingCards from './components/matchmakingCards';
import LicenseLoginModal from './components/LicenseLoginModal';
import { useNavigate } from "react-router-dom";
import { useAlert } from '../../AlertContext';
import { useTranslation } from 'react-i18next';
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import Lottie from "lottie-react";
import Link_Loading from '../../Assets/Link_Loading.json';

const LOCATION_FILTER_KEY = 'spectre_location_filter';

export default function Matchmaking(props: any) {
    const { setSelectedMachine, cookies, setIsLoggedIn, removeCookie, onlineMachines, setOnlineMachines, localMachines, setLocalMachines, setMachineEvents, user } = props
    const [offlineMachines, setOfflineMachines] = useState<Array<OfflineMachine>>([]);
    const [activeCard, setActiveCard] = useState<boolean>(true);
    const [machinesFetched, setMachinesFetched] = useState<boolean>(false);
    const [isLocalNetworkBlocked, setIsLocalNetworkBlocked] = useState<boolean>(false);
    const [licenseModalMachine, setLicenseModalMachine] = useState<Machine | null>(null);
    const { openAlert } = useAlert();
    const { t } = useTranslation();
    const appInsights = useAppInsightsContext();

    const DOMAIN = import.meta.env.VITE_DOMAIN || '.spectre-licensing.com'

    const navigate = useNavigate();

    // --- Dynamic Chrome Settings URL ---
    const getCurrentSiteUrl = () => {
        const currentUrl = `${window.location.protocol}//${window.location.host}/`;
        return `chrome://settings/content/siteDetails?site=${encodeURIComponent(currentUrl)}`;
    };

    // --- Location Filter State ---
    const [locationFilter, setLocationFilter] = useState<string>(() => {
        // Try to load from localStorage, fallback to 'All'
        return localStorage.getItem(LOCATION_FILTER_KEY) || 'All';
    });

    // --- Get unique locations from onlineMachines ---
    const onlineLocations: string[] = Array.from(
        new Set(onlineMachines.map((m: any) => m.location).filter(Boolean))
    );

    // --- Ensure filter is valid when onlineLocations change ---
    useEffect(() => {
        if (onlineLocations.length !== 0) {
            if (locationFilter !== 'All' && !onlineLocations.includes(locationFilter)) {
                setLocationFilter('All');
                localStorage.setItem(LOCATION_FILTER_KEY, 'All');
            }
        } else if ((onlineMachines.length && onlineLocations.length === 0) || (onlineMachines.length && !onlineLocations.includes(locationFilter)) ) {
            // If there are online machines but no locations, reset filter to 'All'
            setLocationFilter('All');
            localStorage.setItem(LOCATION_FILTER_KEY, 'All');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onlineMachines.length]);

    // --- Save filter to localStorage on change ---
    useEffect(() => {
        localStorage.setItem(LOCATION_FILTER_KEY, locationFilter);
    }, [locationFilter]);

    // --- Filtered online machines based on location ---
    const filteredOnlineMachines = locationFilter === 'All'
        ? onlineMachines
        : onlineMachines.filter((m: any) => m.location === locationFilter);

    useEffect(() => {
        const controller = new AbortController();
        (async () => {
            try {
                setOnlineMachines([]);
                setOfflineMachines([]);
                setLocalMachines([]);
                setMachineEvents([]);
                setSelectedMachine(false);
                localStorage.removeItem('onlineMachines');

                const { onlineMachines: nextOnline, localMachines: nextLocal, offlineMachines: nextOffline } = await refreshMatchmakingData(cookies.user64, controller.signal);
                if (controller.signal.aborted) return;
                setOnlineMachines(nextOnline);
                setLocalMachines(nextLocal);
                setOfflineMachines(nextOffline);
                setMachinesFetched(true);

                // If a local machine is online, supports the login API, but no user is
                // logged in, prompt for credentials. Older machines without LoginAPI
                // support are skipped so they never see the modal.
                const unlicensed = nextLocal.find((m) => m.isLicensed === false && m.supportsLoginAPI === true);
                if (unlicensed) setLicenseModalMachine(unlicensed);
            } catch (err) {
                if (controller.signal.aborted) return;
                const isNetworkError = err instanceof TypeError;
                openAlert(
                    t('alertContext.matchmakingFailure.title'),
                    t('alertContext.matchmakingFailure.message'),
                    t('alertContext.back'),
                    () => {
                        console.log(err);
                        if (!isNetworkError) {
                            setIsLoggedIn(false);
                            removeCookie('user64', { path: '/', domain: DOMAIN })
                        }
                        navigate('/login')
                    },
                    false,
                    isNetworkError ? undefined : err as Error,
                    {
                        component: 'Matchmaking',
                        action: 'fetch_machines',
                        user: cookies.user64,
                    }
                );
            }
        })();
        return () => { controller.abort(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        const supportsPermissionsApi =
            typeof navigator !== 'undefined' &&
            'permissions' in navigator &&
            typeof navigator.permissions?.query === 'function';

        if (!supportsPermissionsApi) {
            // Silently ignore on platforms without Permissions API (Android WebView, iOS Safari, etc.)
            return;
        }

        // @ts-ignore - "local-network-access" is not yet in standard Permissions API types
        navigator.permissions.query({ name: "local-network-access" })
            .then((result: any) => {
                if (result.state === 'denied') {
                    setIsLocalNetworkBlocked(true);

                    // Track the event with Azure Application Insights
                    appInsights.trackEvent({
                        name: "chrome-local-network-blocked",
                        properties: {
                            user: user || "anonymous"
                        }
                    });
                }
            })
            .catch(() => {
                // Unsupported or rejected – ignore
            });

    }, [appInsights, cookies.user64, user]);

    const styles = {
        page: {
            backgroundColor: "#F5F5F8"
        },
        text: {
            fontWeight: 700,
            justifyContent: {
                md: 'flex-start',
                xs: 'center'
            }
        },
        gridContainer: {
            alignItems: { xs: 'center', sm: 'center', md: 'unset', lg: 'unset' },
            justifyContent: { xs: 'center', sm: 'center', md: 'flex-start', lg: 'flex-start' }
        }
    }

    return (
        <Grid container sx={styles.page} style={{ paddingTop: 68 }}>
            {isLocalNetworkBlocked && (
                <Grid container justifyContent="center" sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={10} md={8} lg={6}>
                        <Alert severity="warning" sx={{ mx: 2, mb: 1, py: 1, textAlign: 'left' }}>
                            <AlertTitle sx={{ mb: 1, textAlign: 'left' }}>Local Network Access Blocked</AlertTitle>
                            <Box sx={{ textAlign: 'left' }}>
                                Local network access is being blocked. Please update your Chrome settings.
                                <ol style={{ paddingLeft: '20px', marginTop: '10px' }}>
                                    <li style={{ marginBottom: '8px' }}>
                                        Copy and paste the following URL into your Chrome address bar:
                                        <br />
                                        <code>{getCurrentSiteUrl()}</code>
                                    </li>
                                    <li>
                                        Locate the <strong>Local network access</strong> setting and change from <strong>Block</strong> to <strong>Allow</strong>
                                    </li>
                                </ol>
                            </Box>
                        </Alert>
                    </Grid>
                </Grid>
            )}
            {!machinesFetched ?
                <Grid
                    container
                    justifyContent="center"
                    alignItems="center"
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        overflow: 'hidden',
                        pt: 6
                    }}
                >
                    <Lottie animationData={Link_Loading} loop style={{ width: '80%', height: '80%', objectFit: 'contain' }} />
                </Grid>
                :
                <Box sx={{ width: '100%', maxWidth: 2000, mx: 'auto', px: { xs: 2, sm: 3, md: 6, lg: 10, xl: 16 } }}>
                    {onlineMachines.length === 0 && localMachines.length === 0 && offlineMachines.length === 0 ?
                            <Alert severity="info" sx={{ mt: 6, textAlign: 'left', alignItems: 'center', fontSize: '1.15rem', border: '1px solid', borderColor: 'info.main', width: 'fit-content', maxWidth: '100%' }}>
                                {t('matchmaking.noSpectresFound')}<br />
                                {t('matchmaking.noSpectresFoundExplanation')}
                            </Alert>
                            :
                            ""
                    }
                    {onlineMachines.length === 0 && localMachines.length === 0 && offlineMachines.length > 0 ?
                            <Alert severity="info" sx={{ mt: 6, textAlign: 'left', alignItems: 'center', fontSize: '1.15rem', border: '1px solid', borderColor: 'info.main', width: 'fit-content', maxWidth: '100%' }}>
                                {t('matchmaking.noActiveSpectres')}{' '}
                                {t('matchmaking.refreshPrompt')}
                            </Alert>
                            :
                            ""
                    }
                    {localMachines.length > 0 ?
                        <Grid item md={12} container sx={{ mt: 6 }}>
                            <Grid item md={12} container sx={styles.gridContainer}>
                                <Typography variant="h5" color="#262D6D" sx={styles.text}>{t('matchmaking.thisSpectre')}</Typography>
                            </Grid>
                            <Grid item md={12} container>
                                <MatchmakingCards onlineMachines={localMachines} machine={Machine} setSelectedMachine={setSelectedMachine} activeCard={activeCard} setActiveCard={setActiveCard}></MatchmakingCards>
                            </Grid>
                        </Grid>
                        :
                        ""
                    }
                    {onlineMachines.length > 0 ?
                        <Grid item md={12} container sx={{ mt: 6 }}>
                            <Grid container item xs={12} sm={12} md={6} lg={5} sx={styles.gridContainer}>
                                <Typography variant="h5" color="#262D6D" sx={styles.text}>
                                    {t('matchmaking.activeSpectres')}
                                </Typography>
                                <FormControl size="small" sx={{ ml: 2 }}>
                                    <InputLabel id="location-filter-label">{t('matchmaking.filterByLocation')}</InputLabel>
                                    <Select
                                        labelId="location-filter-label"
                                        value={locationFilter}
                                        label={t('matchmaking.filterByLocation')}
                                        onChange={e => setLocationFilter(e.target.value)}
                                    >
                                        <MenuItem value="All">{t('matchmaking.allLocations')}</MenuItem>
                                        {onlineLocations.map(loc => (
                                            <MenuItem key={loc} value={loc}>{loc}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item md={12} container>
                                <MatchmakingCards
                                    onlineMachines={filteredOnlineMachines.filter((machine: Machine) => !(machine.connection instanceof LocalHostConnection))}
                                    machine={Machine}
                                    setSelectedMachine={setSelectedMachine}
                                    activeCard={activeCard}
                                    setActiveCard={setActiveCard}
                                />
                            </Grid>
                        </Grid>
                        :
                        ""
                    }
                    {offlineMachines.length > 0 ?
                        <Grid item md={12} container sx={{ mt: 6 }}>
                            <Grid container item xs={12} sm={12} md={6} lg={4}  sx={styles.gridContainer}>
                                <Typography variant="h5" color="#262D6D" sx={styles.text}>{t('matchmaking.inactiveSpectres')}</Typography>
                            </Grid>
                            <Grid item md={12} container>
                                <MatchmakingCards onlineMachines={offlineMachines} machine={Machine} setSelectedMachine={setSelectedMachine} get_preferred_connection={get_preferred_connection} setOnlineMachines={setOnlineMachines} setLocalMachines={setLocalMachines} setOfflineMachines={setOfflineMachines} cookies={cookies} offlineMachines={offlineMachines}></MatchmakingCards>
                            </Grid>
                        </Grid>
                        :
                        ""
                    }
                </Box>
            }
            <LicenseLoginModal
                open={!!licenseModalMachine}
                machine={licenseModalMachine}
                onClose={() => setLicenseModalMachine(null)}
                onSuccess={(m) => {
                    m.isLicensed = true;
                    setLicenseModalMachine(null);
                }}
            />
        </Grid>
    );
}